"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { useParams } from "next/navigation";
import className from "classnames";

// layouts
import SinglePageLayout from "@/layouts/SinglePageLayout";

// components
import SearchFilters from "@/components/SearchComponents/SearchFilters";
import SearchResultsControls from "./components/SearchResultsControls";
import SearchResultsPageCards from "./components/SearchResultsPageCards";
import Pagination from "@/components/Pagination";
import Select from "@/shared/Select";
import SearchResultsFilterDrawer from "./components/SearchResultsFilterDrawer";

// interfaces
import { ISearchResultsPageComponent } from "./ISearchResultsPageComponent";
import { ICurrency } from "@/types/api/Currency";
import { IPaginatedResponse } from "@/types/api/PaginatedResponse";
import { ISingleSearch } from "@/types/api/SingleSearchResponse";
import { IFacets } from "@/types/api/Facets";
import { IBuildQueryString } from "@/types/helpers/searchHelpers";
import { SelectRef, SingleSelectOption } from "@/shared/Select/ISelect";

// helpers
import {
  DEFAULT_SEARCH_PARAMS,
  PAGE_SIZES_OPTIONS,
  buildRequestQuery,
  transformSearchParams,
} from "./helpers";
import { useDimension } from "@/hooks/useDimensions";
import useCurrency from "@/hooks/useCurrency";
import useQueryParams from "@/hooks/useQueryParams";
import { buildQueryString } from "@/helpers/searchHelpers";
import useUserProfile from "@/hooks/useUserProfile";
import search from "@/api/query/search";
import currencies from "@/api/query/currencies";
import { useCompletedStepModalContext } from "@/contexts/CompletedStepModalContext";
import useStorage from "@/hooks/useStorage";

// assets
import styles from "@/assets/styles/pages/search-results.module.scss";
import SearchCriteriaTags from "@/components/SearchCriteriaTags";

type TQueryParams = {
  [key: string]: unknown;
  page: number;
  pageSize: number;
};

const SearchResultsPageComponent = ({ searchParams = {}, initWidth }: ISearchResultsPageComponent) => {
  const [showMap, setShowMap] = useState(false);
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(!((initWidth as number) < 1200));

  const resultBlockRef = useRef<HTMLHeadingElement | null>(null);
  const selectShowRef = useRef<SelectRef | null>(null); //TODO update later
  const [width] = useDimension({ initWidth });
  const [searchResultsLocationCount, setSearchResultsLocationCount] = useStorage("session", "searchResultsLocationCount");

  const previousPageSize = searchResultsLocationCount
    ? searchResultsLocationCount : DEFAULT_SEARCH_PARAMS.pageSize;

  const [currentCurrency] = useCurrency();
  const { setState: completedStepModalSetState } = useCompletedStepModalContext();
  const { lang } = useParams();

  const [params, setQueryParams] = useQueryParams<TQueryParams>({
    ...DEFAULT_SEARCH_PARAMS,
    pageSize: previousPageSize
  });
  const { page, pageSize, ...queryParams } = params;

  const transformedSearchParams = transformSearchParams(searchParams);

  const defaultOrderingValue = lang === "aus" ? "-weekly_footfall" : "-high_to_low";
  const orderingValue = searchParams.ordering || defaultOrderingValue;

  const { isAuthorized, data: user } = useUserProfile();
  const { data: searchResults, isLoading } = search.useSearch({
    params: buildRequestQuery({
      ...params,
      size__in: params.size__in === "custom" ? null : params.size__in,
      currency: currentCurrency,
      ordering: orderingValue,
    } as unknown as Record<string, string>),
  });

  const { data: currencyList = [] } = currencies.useCurrenciesList();

  const totalPages = Math.ceil((searchResults?.count || 0) / +pageSize);
  const currency = currencyList.find(item => currentCurrency === item?.code);

  const innerClassName = className(
    styles["locl-search-results__inner"],
    {
      [styles["locl-search-results__inner_with-filter"]]: isMoreFiltersOpen,
      [styles["locl-search-results__inner_with-map"]]: showMap,
    }
  );

  // methods
  const toggleMapView = () => {
    setIsMoreFiltersOpen(false);
    setShowMap(state => {
      localStorage.setItem("showMap", `${!state}`);

      return !state;
    });
  };

  const handleFiltersOpenChange = (e: boolean) => {
    if (showMap && width > 1200) {
      toggleMapView();
    }

    setIsMoreFiltersOpen(e);
  }

  const debouncedSetQuery = useCallback(debounce(payload => setQueryParams(payload), 1000), []);

  const onPageChange = (e: number) => {
    setQueryParams({ page: e });
    resultBlockRef?.current?.scrollIntoView({ position: top } as ScrollIntoViewOptions);
  };

  const onPageSizeChange = (e: SingleSelectOption) => {
    setQueryParams({ pageSize: e.value, page: 1 });
    setSearchResultsLocationCount(e.value as string);
  };

  const onFiltersChange = (key: string, value: unknown) => {
    const updatedSearchParams = { ...transformedSearchParams, [key]: value };

    if (key === "size__in") {
      updatedSearchParams.sizeCustom = null;
    }

    if (key === "city__in" && updatedSearchParams.environments) {
      updatedSearchParams.environments = null;
      updatedSearchParams.except_facet = null;
      updatedSearchParams.collections__in = null;
    }

    const queryParamsPayload = buildQueryString(updatedSearchParams as IBuildQueryString).object;

    if (key === "sizeCustom") {
      debouncedSetQuery(queryParamsPayload);

      return;
    }

    setQueryParams(queryParamsPayload);
  };

  // effects
  useEffect(() => {
    const isShowMap = localStorage.getItem("showMap") === "true";
    setShowMap(isShowMap);
    setIsMoreFiltersOpen(isShowMap ? false : !((initWidth as number) < 1200));
  }, []);

  useEffect(() => {
    if (user?.progressBar as number < 2) {
      completedStepModalSetState({ open: true, type: "search", progressBar: 2 });
    }
  }, [JSON.stringify(user)]);

  useEffect(() => {
    if (isMoreFiltersOpen && width && width < 1200) {
      setIsMoreFiltersOpen(false);
    }
  }, [width]);

  useEffect(() => {
    sessionStorage.setItem("lastSearchQuery", JSON.stringify(params));
  }, [params]);

  // renders
  return (
    <SinglePageLayout className={styles["locl-search-results"]}>
      <h1 className={styles["locl-search-results__hidden-title"]}>Search results</h1>
      <div className={styles["locl-search-results__filter_wrap"]}>
        <SearchFilters
          value={transformedSearchParams}
          onChange={onFiltersChange}
          showSearch={false}
          onSearch={() => ({})}
          onMoreFilters={() => setIsMoreFiltersOpen(true)}
          hideFilterKeys={width > 1200 ? [] : ["purpose__in", "size__in", "type__in"]}
          isShowSearchTabs={width > 1200}
        />
      </div>
      <div className={innerClassName}>
        <SearchResultsFilterDrawer
          currentQuery={queryParams}
          currency={currency as ICurrency}
          facets={searchResults?.facets as IFacets}
          open={isMoreFiltersOpen}
          onOpenChange={handleFiltersOpenChange}
        />
        <div className={styles["locl-search-results__inner__main"]}>
          <h3 className={styles["locl-search-results__cards__title"]} ref={resultBlockRef}>{searchResults?.count} Locations</h3>
          <SearchResultsControls
            isAuthorized={isAuthorized}
            searchResults={searchResults}
            showMap={showMap}
            toggleMapView={toggleMapView}
          />
          { width > 1200 &&
            <SearchCriteriaTags queryParams={params as Record<string, string>} onSetQuery={setQueryParams} isClosable />
          }
          <SearchResultsPageCards
            user={user}
            isFetching={isLoading}
            resultData={searchResults || {} as IPaginatedResponse<ISingleSearch>}
            currency={currency as ICurrency}
            showMap={showMap}
            purpose={params?.purpose__in as string || null}
          />
        </div>
      </div>
      <div className={className(
        styles["locl-search-results__pagination"],
        { [styles["locl-search-results__pagination_move-right"]]: isMoreFiltersOpen }
      )}>
        {
          totalPages > 1
            ? (
              <Pagination
                current={+page}
                onChange={onPageChange}
                total={searchResults?.count}
                pageSize={pageSize}
                simple={width < 620}
                data-testid="locl-search-results-pagination"
              />
            )
            : <div/>
        }
        <Select
          label="Show:"
          labelPlacement="left"
          menuPlacement="top"
          classNamePrefix="locl-search-results-select"
          className={styles["locl-search-results-select"]}
          aria-label="locl-search-results-select"
          menuListTestId="locl-search-results-select-list"
          options={PAGE_SIZES_OPTIONS}
          value={{ value: pageSize, label: pageSize, }}
          onChange={onPageSizeChange}
          ref={selectShowRef as SelectRef}
        />
      </div>
    </SinglePageLayout>
  )
}

export default SearchResultsPageComponent;
